import {
  productProjectionFragment,
  productProjectionVariantFragment,
} from '@/graphql/fragments/productProjection';
import { ProductProjectionResult, WithAllVariants } from '@/utils/productProjection';

/**
 * Basic Custom Product projection search
 *
 * Provides the baseline data set for analytics (Product Viewed, Product Added, etc.) and cart line
 * item creation or, in the case of Greeting Cards, a list that can be converted to a
 * `NutsProductProjectionDisplayVariant` collection.
 *
 * Allows us to circumvent webstore backend lookups for product keys or SKUs, since these special
 * products have a `customProduct` type designation; or the hard-coding of such values in `@/lib/`
 * module constants (and in some historical cases, hard-coded analytics values).
 *
 * This could probably be replaced with a more generic projection search (in a more generic module)
 * expecting a simple `$filter` variable, but webfront requires more consolidation now that
 * `NutsProductProjection` adoption has increased and still slightly fragmented.
 */

export interface SearchByCustomProductKeyVariables {
  customProductKey: [string];
  locale?: 'en';
}

export type CustomProductProjectionSearchResult = Extract<ProductProjectionResult, WithAllVariants>;

export interface CustomProductProjectionSearchResponse {
  data: {
    productProjectionSearch: {
      results: CustomProductProjectionSearchResult[];
    };
  };
}

export const CustomProductProjectionSearch = `query CustomProductProjectionSearch(
  $customProductKey: [String!]!
  $locale: Locale = "en"
) {
  productProjectionSearch(
    markMatchingVariants: true
    queryFilters: [
      {
        model: {
          value: {
            path: "variants.attributes.customProduct.key",
            values: $customProductKey
          }
        }
      }
      {
        model: {
          value: {
            path: "variants.attributes.active",
            values: ["true"]
          }
        }
      }
    ]
  ) {
    results {
      allVariants(onlyMatching: true) {
        ...productProjectionVariantFragment
      }
      ...productProjectionFragment
    }
  }
}

${productProjectionFragment}

${productProjectionVariantFragment}
`;

/**
 * Support for Custom Product PDPs
 *
 * We typically only have a single product with multiple variants, but Custom Trays uses two different
 * products with a single variant each. The query must return potentially multiple products, for call
 * sites to flatten if necessary.
 *
 * These are still a form of projection search, but with additional details that would not make sense
 * for a standard projection search. As noted above, this helps us circumvent webstore backend lookups
 * for product keys or SKUs, since these special products have a `customProduct` type designation; or
 * the hard-coding of such values in `@/lib/` module constants (and in some historical cases, hard-coded
 * analytics values).
 */

export interface DetailProductsByCustomProductKeyVariables {
  filter: string;
  limit?: number;
  locale?: string;
}

interface Category {
  id: string;
  name: string;
  custom: {
    customFieldsRaw: [
      {
        name: 'urlName';
        value: string;
      },
    ];
  };
}

export type DetailProductsByCustomProductKeyProjectionResult = Extract<
  ProductProjectionResult,
  WithAllVariants
> & {
  description: string;
  categories: (Category & {
    ancestors: Category[];
  })[];
};

export interface DetailProductsByCustomProductKeyProjectionSearchResponse {
  data: {
    productProjectionSearch: {
      results: DetailProductsByCustomProductKeyProjectionResult[];
    };
  };
}

export const DetailProductsByCustomProductKey = `
query ProductsByCustomProductKey($filter: String!, $locale: Locale = "en", $limit: Int = 500) {
  productProjectionSearch(
    filters: [
      { string: $filter },
      { string: "variants.attributes.active:true" }
    ],
    limit: $limit,
    markMatchingVariants: true
    sorts: ["variants.attributes.weight asc"]
  ) {
    results {
      ...productProjectionFragment

      allVariants(onlyMatching: true) {
        ...productProjectionVariantFragment
      }

      categories {
        ...categoryFields
        ancestors {
          ...categoryFields
        }
      }

      description(locale: $locale)
    }
  }
}

fragment categoryFields on Category {
  id
  name(locale: $locale)
  custom {
    customFieldsRaw(includeNames: ["urlName"]) {
      name
      value
    }
  }
}

${productProjectionFragment}

${productProjectionVariantFragment}
`;
